import helpersUtils from 'utils/Helpers';
import localeUtils from 'utils/LanguageLocale';
import HistoryTabEN from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_en_US';
import HistoryTabFR from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_fr_CA';

const { formatPrice } = helpersUtils;
const defaultGetText = localeUtils.getLocalisedDataFromResource(HistoryTabEN, HistoryTabFR);

/**
 * Display order for supported Year at a Glance activity types.
 *
 * Employee discount activity leads the section, and credit-card rewards are omitted later for CA users.
 *
 * @type {string[]}
 */
const YEAR_AT_GLANCE_ORDER = [
    'employeeDiscountPurchase',
    'promosAndDiscountApplied',
    'beautyInsiderCashApplied',
    'creditCardRewardsEarned',
    'rougeRewardsEarned'
];

const SOURCE_TO_TYPE = {
    employeeDiscountPurchase: 'employeeDiscountPurchase',
    employeeDiscount: 'employeeDiscountPurchase',
    dollarsSaved: 'promosAndDiscountApplied',
    promosAndDiscountApplied: 'promosAndDiscountApplied',
    cashApplied: 'beautyInsiderCashApplied',
    beautyInsiderCashApplied: 'beautyInsiderCashApplied',
    ccRewards: 'creditCardRewardsEarned',
    creditCardRewardsEarned: 'creditCardRewardsEarned',
    rougeRcDollar: 'rougeRewardsEarned',
    rougeRewardsEarned: 'rougeRewardsEarned'
};

const numericStringPattern = /^-?\d+(\.\d+)?$/;

const formatDollarValue = value => {
    if (typeof value === 'string') {
        const trimmedValue = value.trim();

        return numericStringPattern.test(trimmedValue) ? formatPrice(`$${trimmedValue}`) : value;
    }

    if (typeof value === 'number') {
        return formatPrice(`$${value}`);
    }

    return null;
};

const getYearAtGlanceLabel = (type, getText) => {
    const translatedLabel = getText?.(type);

    return translatedLabel || type;
};

const hasPositiveValue = item => {
    const rawValue = item.rawValue ?? item.amount ?? item.value ?? item.text;

    if (item.type === 'employeeDiscountPurchase') {
        return Boolean(rawValue);
    }

    if (typeof rawValue === 'number') {
        return rawValue > 0;
    }

    if (typeof rawValue === 'string') {
        const numericValue = Number(rawValue.replace(/[^0-9.-]/g, ''));

        return Number.isNaN(numericValue) || numericValue > 0;
    }

    return Boolean(item.value || item.text);
};

const normalizeRawItems = (rawItems, getText) => {
    if (Array.isArray(rawItems)) {
        return rawItems;
    }

    const currentYear = rawItems?.currentYear || rawItems || {};
    const bankRewards = rawItems?.bankRewards || {};
    const items = Object.keys(SOURCE_TO_TYPE).reduce((acc, sourceKey) => {
        const sourceItem = currentYear[sourceKey];

        if (!sourceItem) {
            return acc;
        }

        acc.push({
            type: SOURCE_TO_TYPE[sourceKey],
            label: getYearAtGlanceLabel(SOURCE_TO_TYPE[sourceKey], getText),
            value: sourceItem.text || formatDollarValue(sourceItem.value),
            rawValue: sourceItem.value,
            subtitle: sourceItem.subtitle
        });

        return acc;
    }, []);

    const rewardsTotal = bankRewards?.ytdRewardsEarned || bankRewards?.YTDRewardsEarned;

    if (rewardsTotal) {
        items.push({
            type: 'creditCardRewardsEarned',
            label: getYearAtGlanceLabel('creditCardRewardsEarned', getText),
            value: formatDollarValue(rewardsTotal),
            rawValue: rewardsTotal
        });
    }

    return items;
};

/**
 * Normalizes annual BI summary activity into ordered Year at a Glance presentation items.
 *
 * @param {Object} params
 * @param {Array|Object} params.rawItems Either an already-normalized item array or an API summary object containing
 * currentYear metrics and optional bankRewards totals.
 * @param {'US'|'CA'} [params.region='US'] Region filter. CA excludes credit-card rewards activity.
 * @param {Function} [params.getText] Localized label lookup keyed by normalized activity type.
 * @returns {Array<{type: string, label: string, value: string, subtitle: string}>} Ordered display items with known
 * positive values only.
 */
const mapHistoryYearAtGlanceItems = ({ rawItems, region = 'US', getText = defaultGetText } = {}) => {
    const rawItemList = normalizeRawItems(rawItems, getText);
    const knownItems = rawItemList
        .map(item => {
            const type = SOURCE_TO_TYPE[item.type] || item.type;

            return {
                type,
                label: item.label || getYearAtGlanceLabel(type, getText),
                value: item.value || item.text || formatDollarValue(item.amount),
                subtitle: item.subtitle
            };
        })
        .filter(item => YEAR_AT_GLANCE_ORDER.includes(item.type))
        .filter(item => item.value)
        .filter(hasPositiveValue)
        .filter(item => !(region === 'CA' && item.type === 'creditCardRewardsEarned'));

    return YEAR_AT_GLANCE_ORDER.reduce((orderedItems, type) => {
        const item = knownItems.find(currentItem => currentItem.type === type);

        if (item) {
            orderedItems.push(item);
        }

        return orderedItems;
    }, []);
};

export {
    YEAR_AT_GLANCE_ORDER, mapHistoryYearAtGlanceItems
};
